/* eslint-disable fp/no-mutation */
import { useFormik } from 'formik'
import * as yup from 'yup'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { ReactElement, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import axios from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchCourses,
  fetchLinkedServices,
  fetchSections,
  fetchTemplates,
  selectLinkedServices,
  selectLinkedServicesStatus,
  selectSections,
  selectSectionsStatus,
  selectTemplatesByLinkedServiceID,
} from 'src/v3/create/create.slice'
import { RightArrowIcon, AddIcon, EditIcon } from 'src/v3/core/icons'
import { Course } from 'src/v3/core/types'
import { selectRoles } from 'src/v3/settings/settings.slice'
import Loading from 'src/v3/components/loading'
import ImageUpload from 'src/v3/components/upload-image'
import { uploadImageToS3 } from 'src/v3/utilities/s3'
import CategoryModal from './components/category-modal'

interface Props {
  course?: Course
  setStep: React.Dispatch<
    React.SetStateAction<
      'basic-info' | 'classes' | 'accreditations' | 'publishing'
    >
  >
  onCourseCreated?: (course: any) => void
}

export default ({ course, setStep, onCourseCreated }: Props): ReactElement => {
  let dispatch = useAppDispatch()
  let [searchParams] = useSearchParams()

  let roles = useAppSelector(selectRoles)
  let isAdmin = roles.includes('admin')

  // Category creation modal state
  let [categoryModalOpen, setCategoryModalOpen] = useState<boolean>(false)
  let [editingSectionId, setEditingSectionId] = useState<string | undefined>(
    undefined,
  )

  // Template creation modal state
  let [templateModalOpen, setTemplateModalOpen] = useState<boolean>(false)
  let [creatingTemplate, setCreatingTemplate] = useState<boolean>(false)
  let [templateError, setTemplateError] = useState<string>('')

  // Get pre-selected values from URL params (from landing page)
  let preSelectedLinkedService = searchParams.get('linkedService') || ''
  let preSelectedSection = searchParams.get('section') || ''

  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let sections = useAppSelector(selectSections)
  let sectionsStatus = useAppSelector(selectSectionsStatus)

  let [loading, setLoading] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')
  let [thumbnail, setThumbnail] = useState<File | undefined>(undefined)

  let form = useFormik({
    validationSchema: yup.object({
      linkedServiceId: yup.string().required('Linked service is required'),
      sectionId: yup.string().required('Section is required'),
      name: yup.string().required('Name is required'),
      displayName: yup.string().required('Display name is required'),
      hours: yup
        .number()
        .min(0, 'Hours must be 0 or greater')
        .required('Hours is required'),
      templateId: yup
        .string()
        .notOneOf(['unselected'], 'Please select a template')
        .required('Template is required'),
    }),
    enableReinitialize: true,
    initialValues: {
      linkedServiceId: course
        ? course.linkedServiceID || 'FOAMfrat'
        : preSelectedLinkedService || (isAdmin ? 'FOAMfrat' : ''),
      sectionId: course?.sectionID || preSelectedSection || '',
      name: course?.name || '',
      displayName: course?.displayName || '',
      hours: course?.length || 0,
      templateId: course?.templateID || 'unselected',
    },
    onSubmit: async (values) => {
      setLoading(true)
      try {
        let savedCourse
        if (course) {
          // Update existing course
          let response = await axios.put('/api/v2/create/course', {
            id: course.id,
            name: values.name,
            displayName: values.displayName,
            length: values.hours,
            templateId: values.templateId,
            sectionId: values.sectionId,
            linkedServiceId:
              values.linkedServiceId.toLowerCase() !== 'foamfrat'
                ? values.linkedServiceId
                : null,
          })
          savedCourse = response.data
        } else {
          // Create new course
          let response = await axios.post('/api/v2/create/course', {
            name: values.name,
            displayName: values.displayName,
            length: values.hours,
            templateId: values.templateId,
            sectionId: values.sectionId,
            linkedServiceId:
              values.linkedServiceId.toLowerCase() !== 'foamfrat'
                ? values.linkedServiceId
                : null,
          })
          savedCourse = response.data
        }

        // Upload thumbnail if provided
        if (thumbnail && (savedCourse?.id || course?.id)) {
          // Convert File to base64 string
          let thumbnailBase64 = await new Promise<string>((resolve) => {
            let reader = new FileReader()
            reader.onloadend = () => resolve(reader.result as string)
            reader.readAsDataURL(thumbnail)
          })

          // Get signed URL and upload to S3
          await axios
            .put('/api/v2/create/course/image', {
              id: savedCourse?.id || course?.id,
            })
            .then((uploadResponse) =>
              uploadImageToS3(thumbnailBase64, uploadResponse.data),
            )
        }

        // Refresh courses to ensure Redux state is updated first
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchCourses())

        // If we just created a new course, call the callback to handle step progression
        if (!course && savedCourse && onCourseCreated) {
          onCourseCreated(savedCourse)
          return
        }

        // For editing existing courses, just go to next step
        setStep('classes')
      } catch (err: any) {
        setErrorMessage(
          err?.response?.data || err?.message || 'Error saving course',
        )
      } finally {
        setLoading(false)
      }
    },
  })

  let templates = useAppSelector(
    selectTemplatesByLinkedServiceID(form.values.linkedServiceId),
  )

  // Get filtered sections based on selected linked service
  let filteredSections = sections.filter((section) => {
    if (form.values.linkedServiceId === 'FOAMfrat') {
      // FOAMfrat sections have linkedServiceID of null
      return !section.linkedServiceID
    }
    return section.linkedServiceID === form.values.linkedServiceId
  })

  // Handlers for category modal
  let handleEditSection = (sectionId: string) => {
    setEditingSectionId(sectionId)
    setCategoryModalOpen(true)
  }

  let handleCategoryModalSuccess = (sectionId: string) => {
    dispatch(fetchSections())
    form.setFieldValue('sectionId', sectionId)
    setCategoryModalOpen(false)
    setEditingSectionId(undefined)
  }

  let handleCategoryModalClose = () => {
    setCategoryModalOpen(false)
    setEditingSectionId(undefined)
  }

  // Template creation form
  let templateForm = useFormik({
    initialValues: {
      name: '',
      statement1: '',
      linkedServiceId:
        form.values.linkedServiceId || (isAdmin ? 'FOAMfrat' : ''),
    },
    validationSchema: yup.object({
      name: yup.string().required('Template name is required'),
      statement1: yup.string().required('Statement is required'),
    }),
    onSubmit: async (values) => {
      setCreatingTemplate(true)
      setTemplateError('')
      try {
        let response = await axios.post('/api/v2/create/template', {
          name: values.name,
          statement1: values.statement1,
          statement2: '',
          linkedServiceId:
            values.linkedServiceId.toLowerCase() !== 'foamfrat'
              ? values.linkedServiceId
              : null,
        })

        let newTemplate = response.data

        // Refresh templates and select the new one
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchTemplates())
        form.setFieldValue('templateId', newTemplate.id)

        // Close modal and reset form
        setTemplateModalOpen(false)
        templateForm.resetForm()
      } catch (err: any) {
        setTemplateError(
          err?.response?.data || err?.message || 'Error creating template',
        )
      } finally {
        setCreatingTemplate(false)
      }
    },
  })

  useEffect(() => {
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [linkedServicesStatus])

  useEffect(() => {
    if (sectionsStatus === 'uninitialized') {
      dispatch(fetchSections())
    }
  }, [sectionsStatus])

  return (
    <Box>
      <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
        Basic Course Information
      </Typography>
      <form onSubmit={form.handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {!course && (
            <Box sx={{ display: 'flex', gap: 3 }}>
              <FormControl
                sx={{ flex: 1 }}
                error={
                  form.touched.linkedServiceId && !!form.errors.linkedServiceId
                }
              >
                <InputLabel>Linked Service</InputLabel>
                <Select
                  value={form.values.linkedServiceId}
                  label="Linked Service"
                  name="linkedServiceId"
                  onChange={form.handleChange}
                  disabled={loading}
                >
                  <MenuItem value="">Select Linked Service</MenuItem>
                  {isAdmin && <MenuItem value="FOAMfrat">FOAMfrat</MenuItem>}
                  {linkedServices.map((ls) => (
                    <MenuItem key={ls.id} value={ls.id}>
                      {ls.name}
                    </MenuItem>
                  ))}
                </Select>
                {form.touched.linkedServiceId &&
                  form.errors.linkedServiceId && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, ml: 1.5 }}
                    >
                      {form.errors.linkedServiceId}
                    </Typography>
                  )}
              </FormControl>

              <Box
                sx={{
                  display: 'flex',
                  flex: 1,
                  gap: 1,
                  alignItems: 'flex-start',
                }}
              >
                <FormControl
                  sx={{ flex: 1 }}
                  error={form.touched.sectionId && !!form.errors.sectionId}
                >
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={form.values.sectionId}
                    label="Category"
                    name="sectionId"
                    onChange={(e) => {
                      if (e.target.value === 'CREATE_NEW') {
                        setEditingSectionId(undefined)
                        setCategoryModalOpen(true)
                      } else {
                        form.handleChange(e)
                      }
                    }}
                    disabled={loading || !form.values.linkedServiceId}
                  >
                    <MenuItem value="">Select Category</MenuItem>
                    <MenuItem
                      value="CREATE_NEW"
                      sx={{ color: 'primary.main', fontWeight: 'medium' }}
                    >
                      <AddIcon sx={{ mr: 1, fontSize: 16 }} />
                      Create New Category
                    </MenuItem>
                    {filteredSections.map((section) => (
                      <MenuItem key={section.id} value={section.id}>
                        {section.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {form.touched.sectionId && form.errors.sectionId && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, ml: 1.5 }}
                    >
                      {form.errors.sectionId}
                    </Typography>
                  )}
                </FormControl>

                {form.values.sectionId && form.values.sectionId !== '' && (
                  <Tooltip title="Edit Category">
                    <IconButton
                      onClick={() => handleEditSection(form.values.sectionId)}
                      color="primary"
                      sx={{ mt: 1 }}
                      aria-label="Edit selected category"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          )}

          <TextField
            fullWidth
            label="Course Name"
            name="name"
            value={form.values.name}
            onChange={form.handleChange}
            error={form.touched.name && !!form.errors.name}
            helperText={
              form.touched.name && form.errors.name
                ? form.errors.name
                : 'Primary name of the course; used for accreditation'
            }
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Display Name"
            name="displayName"
            value={form.values.displayName}
            onChange={form.handleChange}
            error={form.touched.displayName && !!form.errors.displayName}
            helperText={
              form.touched.displayName && form.errors.displayName
                ? form.errors.displayName
                : 'Alternate name for the course shown to students'
            }
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Hours"
            name="hours"
            type="number"
            value={form.values.hours}
            onChange={form.handleChange}
            error={form.touched.hours && !!form.errors.hours}
            helperText={
              form.touched.hours && form.errors.hours
                ? form.errors.hours
                : 'Course length in hours'
            }
            disabled={loading}
            slotProps={{
              htmlInput: { min: 0, step: 0.1 },
            }}
          />

          <FormControl
            fullWidth
            error={form.touched.templateId && !!form.errors.templateId}
          >
            <InputLabel>Template</InputLabel>
            <Select
              value={form.values.templateId}
              label="Template"
              name="templateId"
              onChange={(e) => {
                if (e.target.value === 'CREATE_NEW') {
                  templateForm.setFieldValue(
                    'linkedServiceId',
                    form.values.linkedServiceId,
                  )
                  setTemplateModalOpen(true)
                } else {
                  form.handleChange(e)
                }
              }}
              disabled={loading || !form.values.linkedServiceId}
            >
              <MenuItem value="unselected">Select a template</MenuItem>
              <MenuItem
                value="CREATE_NEW"
                sx={{ color: 'primary.main', fontWeight: 'medium' }}
              >
                <AddIcon sx={{ mr: 1, fontSize: 16 }} />
                Create New Template
              </MenuItem>
              {templates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name}
                </MenuItem>
              ))}
            </Select>
            {form.touched.templateId && form.errors.templateId && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.5, ml: 1.5 }}
              >
                {form.errors.templateId}
              </Typography>
            )}
          </FormControl>

          {/* Course Thumbnail Upload */}
          <Box>
            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              Course Thumbnail
            </Typography>
            <ImageUpload
              id="course-thumbnail"
              type="thumbnail"
              image={thumbnail}
              setImage={setThumbnail}
              existingImage={course?.imageName}
              imageBasePath="public"
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              type="submit"
              disabled={loading}
              endIcon={<RightArrowIcon />}
            >
              {course ? 'Update & Continue' : 'Create & Continue'}
            </Button>
          </Box>
        </Box>
      </form>
      {/* Category Modal (Create/Edit) */}
      <CategoryModal
        open={categoryModalOpen}
        onClose={handleCategoryModalClose}
        onSuccess={handleCategoryModalSuccess}
        editingSectionId={editingSectionId}
        linkedServiceId={form.values.linkedServiceId}
      />
      {/* Template Creation Modal */}
      <Dialog
        open={templateModalOpen}
        onClose={() => !creatingTemplate && setTemplateModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <Paper sx={{ px: 4, py: 2 }}>
          <form onSubmit={templateForm.handleSubmit}>
            <DialogTitle sx={{ textAlign: 'center' }}>
              Create New Template
            </DialogTitle>
            <DialogContent sx={{ px: 2, pb: 6 }}>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}
              >
                <TextField
                  fullWidth
                  label="Template Name"
                  name="name"
                  value={templateForm.values.name}
                  onChange={templateForm.handleChange}
                  error={
                    templateForm.touched.name && !!templateForm.errors.name
                  }
                  helperText={
                    templateForm.touched.name && templateForm.errors.name
                  }
                  disabled={creatingTemplate}
                  slotProps={{
                    input: {
                      sx: { bgcolor: 'background.paper' },
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Statement"
                  name="statement1"
                  multiline
                  rows={3}
                  value={templateForm.values.statement1}
                  onChange={templateForm.handleChange}
                  error={
                    templateForm.touched.statement1 &&
                    !!templateForm.errors.statement1
                  }
                  helperText={
                    templateForm.touched.statement1 &&
                    templateForm.errors.statement1
                  }
                  disabled={creatingTemplate}
                  slotProps={{
                    input: {
                      sx: { bgcolor: 'background.paper' },
                    },
                  }}
                />

                {templateError && (
                  <Alert severity="error">{templateError}</Alert>
                )}
              </Box>
            </DialogContent>
            <DialogActions
              disableSpacing={true}
              sx={{ flexDirection: 'column', gap: 2, pt: 0, pb: 6, mt: 0 }}
            >
              <Button
                type="submit"
                color="primary"
                variant="contained"
                fullWidth
                disabled={creatingTemplate}
              >
                {creatingTemplate ? 'Creating...' : 'Create Template'}
              </Button>
              <Button
                onClick={() => {
                  setTemplateModalOpen(false)
                  templateForm.resetForm()
                }}
                color="error"
                variant="contained"
                fullWidth
                disabled={creatingTemplate}
              >
                Cancel
              </Button>
            </DialogActions>
          </form>
        </Paper>
      </Dialog>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Loading
        entities={[linkedServicesStatus, sectionsStatus]}
        loading={loading}
      />
    </Box>
  )
}
